"use client"

import { useState, useMemo } from "react"
import { motion } from "framer-motion"
import { useInView } from "react-intersection-observer"
import { Users, Award, Clock, MapPin, Sparkles, Star, Heart, ChefHat, Target, Trophy, Quote, CheckCircle } from "lucide-react"

import { useLanguage } from "../contexts/LanguageContext"
import Footer from "../components/Footer"

const About = () => {
    const { t } = useLanguage()
    const [isLoading] = useState(false)

    const [ref, inView] = useInView({
        triggerOnce: true,
        threshold: 0.1,
        rootMargin: '0px 0px -100px 0px',
    })

    // Move stats array outside component or use useMemo to prevent recreation
    const stats = useMemo(() => [
        { icon: Users, value: "5000+", label: t('stats.happyCustomers') },
        { icon: Award, value: "4.3", label: t('stats.starRating') },
        { icon: Clock, value: "5", label: t('stats.yearsExcellence') },
        { icon: MapPin, value: "1", label: t('home.location.title') },
    ], [t])

    // Memoize subtitle to prevent unnecessary re-renders
    const subtitle = useMemo(() =>
        t('about.subtitle') || 'From exceptional dining to endless entertainment, discover the passionate story behind our 24/7 culinary and entertainment experience.',
        [t]
    )

    if (isLoading) {
        return (
            <div className="pt-16 lg:pt-20 min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 flex items-center justify-center">
                <motion.div
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="text-center"
                >
                    <div className="relative w-20 h-20 mx-auto mb-6">
                        <motion.div
                            animate={{ rotate: 360 }}
                            transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                            className="w-20 h-20 border-4 border-primary-200 border-t-primary-600 rounded-full"
                        />
                        <div className="absolute inset-0 flex items-center justify-center">
                            <ChefHat className="w-8 h-8 text-primary-600" />
                        </div>
                    </div>
                    <p className="text-lg text-gray-600 dark:text-gray-300 font-medium">
                        {t('about.loadingOurStory')}
                    </p>
                </motion.div>
            </div>
        )
    }

    return (
        <div className="pt-16 lg:pt-20">
            {/* Enhanced Header */}
            <section className="relative section-padding bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 overflow-hidden">
                {/* Background Decorations */}
                <div className="absolute inset-0">
                    <div className="absolute top-20 left-10 w-64 h-64 bg-primary/5 rounded-full blur-2xl"></div>
                    <div className="absolute bottom-20 right-10 w-80 h-80 bg-accent/5 rounded-full blur-2xl"></div>
                    <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-secondary/3 rounded-full blur-2xl"></div>

                    {/* Pattern Overlay */}
                    <div className="absolute inset-0 opacity-5">
                        <div className="w-full h-full" style={{
                            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.05'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
                        }}></div>
                    </div>
                </div>

                <div className="relative container mx-auto max-w-7xl container-padding text-center">
                    <motion.div
                        ref={ref}
                        initial={{ opacity: 0, y: 50 }}
                        animate={inView ? { opacity: 1, y: 0 } : {}}
                        transition={{ duration: 0.8 }}
                    >
                        {/* Badge */}
                        <motion.div
                            initial={{ opacity: 0, scale: 0.8 }}
                            animate={inView ? { opacity: 1, scale: 1 } : {}}
                            transition={{ duration: 0.6, delay: 0.2 }}
                            className="inline-flex items-center space-x-2 bg-primary/10 text-primary-400 px-6 py-3 rounded-full text-sm font-medium mb-8"
                        >
                            <ChefHat className="w-5 h-5" />
                            <span>{t('about.ourStory')}</span>
                        </motion.div>

                        <h1 className="text-3xl md:text-4xl lg:text-5xl font-serif font-bold mb-6 text-white leading-tight">
                            {t('about.discoverOurJourney')}
                        </h1>
                        <p className="text-lg md:text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed font-light mb-12">
                            {subtitle}
                        </p>

                        {/* Stats Preview */}
                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            animate={inView ? { opacity: 1, y: 0 } : {}}
                            transition={{ duration: 0.8, delay: 0.4 }}
                            className="flex justify-center items-center space-x-8 flex-wrap gap-4"
                        >
                            <div className="flex items-center space-x-2 bg-white/5 px-4 py-2 rounded-full">
                                <Award className="w-5 h-5 text-yellow-500" />
                                <span className="text-sm text-gray-300 font-medium">{t('about.awardWinner')}</span>
                            </div>
                            <div className="flex items-center space-x-2 bg-white/5 px-4 py-2 rounded-full">
                                <Users className="w-5 h-5 text-primary-400" />
                                <span className="text-sm text-gray-300 font-medium">5000+ {t('about.customers')}</span>
                            </div>
                            <div className="flex items-center space-x-2 bg-white/5 px-4 py-2 rounded-full">
                                <Clock className="w-5 h-5 text-accent-500" />
                                <span className="text-sm text-gray-300 font-medium">5+ {t('about.yearsExcellence')}</span>
                            </div>
                        </motion.div>
                    </motion.div>
                </div>
            </section>

            {/* Enhanced Story Section */}
            <section className="relative section-padding bg-gradient-to-br from-gray-50 via-white to-gray-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
                {/* Background Decorations */}
                <div className="absolute inset-0">
                    <div className="absolute top-32 left-20 w-64 h-64 bg-primary/3 rounded-full blur-xl"></div>
                    <div className="absolute bottom-32 right-20 w-80 h-80 bg-accent/3 rounded-full blur-xl"></div>
                </div>

                <div className="relative container mx-auto max-w-7xl container-padding">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
                        <motion.div
                            initial={{ opacity: 0, x: -50 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.8 }}
                            viewport={{ once: true }}
                            className="space-y-8"
                        >
                            {/* Badge */}
                            <div className="inline-flex items-center space-x-2 bg-primary/10 text-primary-600 px-4 py-2 rounded-full text-sm font-medium">
                                <Quote className="w-4 h-4" />
                                <span>{t('about.ourBeginning')}</span>
                            </div>

                            <h2 className="text-3xl md:text-4xl font-serif font-bold mb-6 leading-tight text-gray-900 dark:text-white">
                                {t('about.culinaryJourney')}
                            </h2>

                            <div className="space-y-6 text-gray-600 dark:text-gray-300 leading-relaxed">
                                <p className="text-lg">
                                    {t('about.uniqueEstablishment')}
                                </p>
                                <p>
                                    {t('about.storyBegins')}
                                </p>
                                <p>
                                    {t('about.todayWePride')}
                                </p>
                            </div>

                            {/* Key Points */}
                            <div className="space-y-4">
                                {[
                                    { icon: CheckCircle, text: t('about.authenticRecipes') },
                                    { icon: CheckCircle, text: t('about.freshSpices') },
                                    { icon: CheckCircle, text: t('about.serviceAvailability') }
                                ].map((point, index) => (
                                    <motion.div
                                        key={index}
                                        initial={{ opacity: 0, x: -20 }}
                                        whileInView={{ opacity: 1, x: 0 }}
                                        transition={{ duration: 0.6, delay: index * 0.1 }}
                                        viewport={{ once: true }}
                                        className="flex items-center space-x-3"
                                    >
                                        <point.icon className="w-5 h-5 text-primary-600 flex-shrink-0" />
                                        <span className="text-gray-700 dark:text-gray-300">{point.text}</span>
                                    </motion.div>
                                ))}
                            </div>
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0, x: 50 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.8 }}
                            viewport={{ once: true }}
                            className="relative"
                        >
                            <div className="relative overflow-hidden rounded-3xl shadow-2xl group">
                                <img
                                    src="/images/interior.jpg"
                                    alt={t('alt.restaurantInterior')}
                                    className="w-full h-[500px] object-cover group-hover:scale-105 transition-transform duration-700"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent"></div>

                                {/* Floating Info Cards */}
                                <div className="absolute top-6 right-6 bg-white/90 dark:bg-gray-900/90 rounded-2xl p-4 shadow-xl">
                                    <div className="flex items-center space-x-2">
                                        <Trophy className="w-5 h-5 text-yellow-500" />
                                        <span className="text-sm font-semibold text-gray-900 dark:text-white">{t('about.awardWinner')}</span>
                                    </div>
                                </div>
                            </div>

                            {/* Established Card */}
                            <motion.div
                                initial={{ opacity: 0, scale: 0.8 }}
                                whileInView={{ opacity: 1, scale: 1 }}
                                transition={{ duration: 0.6, delay: 0.3 }}
                                viewport={{ once: true }}
                                className="absolute -bottom-8 -left-8 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-3xl p-8 shadow-2xl"
                            >
                                <div className="text-center">
                                    <div className="text-4xl font-bold text-primary-600 mb-2">2019</div>
                                    <div className="text-sm text-gray-600 dark:text-gray-400 font-medium">{t('about.established')}</div>
                                    <div className="text-xs text-gray-500 dark:text-gray-500 mt-1">Goma, Congo-Kinshasa</div>
                                </div>
                            </motion.div>

                            {/* Decorative Elements */}
                            <div className="absolute -top-4 -right-4 w-8 h-8 bg-gradient-to-br from-primary-400 to-accent-400 rounded-full opacity-60"></div>
                            <div className="absolute -bottom-4 -right-4 w-6 h-6 bg-gradient-to-br from-accent-400 to-secondary-400 rounded-full opacity-60"></div>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* Enhanced Stats Section */}
            <section className="relative section-padding bg-gradient-to-br from-white via-gray-50 to-white dark:from-gray-800 dark:via-gray-900 dark:to-gray-800">
                {/* Background Pattern */}
                <div className="absolute inset-0 opacity-5">
                    <div className="w-full h-full" style={{
                        backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%2338A9E8' fill-opacity='0.1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
                    }}></div>
                </div>

                <div className="relative container mx-auto max-w-7xl container-padding">
                    {/* Header */}
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                        viewport={{ once: true }}
                        className="text-center mb-16"
                    >
                        <div className="inline-flex items-center space-x-2 bg-primary/10 text-primary-600 px-4 py-2 rounded-full text-sm font-medium mb-6">
                            <Target className="w-4 h-4" />
                            <span>{t('about.ourAchievements')}</span>
                        </div>
                        <h2 className="text-2xl md:text-3xl font-serif font-bold text-gray-900 dark:text-white mb-4">
                            {t('about.numbersTellStory')}
                        </h2>
                        <p className="text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
                            {t('about.milestonesRepresent')}
                        </p>
                    </motion.div>

                    {/* Stats Grid */}
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
                        {stats.map((stat, _) => (
                            <motion.div
                                key={stat.label}
                                initial={{ opacity: 0, y: 30, scale: 0.9 }}
                                whileInView={{ opacity: 1, y: 0, scale: 1 }}
                                transition={{ duration: 0.6, delay: 0.1 }}
                                viewport={{ once: true }}
                                className="text-center group"
                            >
                                <div className="relative mb-6">
                                    <div className="w-24 h-24 bg-gradient-to-br from-primary-100 to-accent-100 dark:from-primary-900/30 dark:to-accent-900/30 rounded-3xl flex items-center justify-center mx-auto group-hover:scale-110 transition-all duration-300 shadow-lg">
                                        <stat.icon className="w-12 h-12 text-primary-600" />
                                    </div>
                                    <div className="absolute -top-2 -right-2 w-6 h-6 bg-gradient-to-br from-accent-400 to-secondary-400 rounded-full opacity-80"></div>
                                </div>

                                <motion.div
                                    initial={{ scale: 0 }}
                                    whileInView={{ scale: 1 }}
                                    transition={{ duration: 0.8, delay: 0.3 }}
                                    viewport={{ once: true }}
                                    className="text-3xl md:text-4xl font-bold bg-gradient-to-br from-gray-900 to-gray-700 dark:from-gray-100 dark:to-gray-300 bg-clip-text text-transparent mb-3"
                                >
                                    {stat.value}
                                </motion.div>

                                <div className="text-sm font-medium text-gray-600 dark:text-gray-400 leading-tight">
                                    {stat.label}
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Enhanced Values Section */}
            <section className="relative section-padding bg-gradient-to-br from-gray-50 via-white to-gray-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
                {/* Background Decorations */}
                <div className="absolute inset-0">
                    <div className="absolute top-32 left-20 w-64 h-64 bg-secondary/3 rounded-full blur-xl"></div>
                    <div className="absolute bottom-32 right-20 w-80 h-80 bg-primary/3 rounded-full blur-xl"></div>
                </div>

                <div className="relative container mx-auto max-w-7xl container-padding">
                    <motion.div
                        initial={{ opacity: 0, y: 50 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                        viewport={{ once: true }}
                        className="text-center mb-20"
                    >
                        <div className="inline-flex items-center space-x-2 bg-accent/10 text-accent-600 px-4 py-2 rounded-full text-sm font-medium mb-6">
                            <Heart className="w-4 h-4" />
                            <span>{t('about.ourValues')}</span>
                        </div>
                        <h2 className="text-3xl md:text-4xl font-serif font-bold mb-6 leading-tight text-gray-900 dark:text-white">
                            {t('about.whatDrivesUs')}
                        </h2>
                        <p className="text-lg text-gray-600 dark:text-gray-300 max-w-3xl mx-auto leading-relaxed">
                            {t('about.coreValuesShape')}
                        </p>
                    </motion.div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-12">
                        {[
                            {
                                title: t('about.authenticity'),
                                description: t('about.authenticityDescription'),
                                icon: Sparkles,
                                color: "from-primary-500 to-primary-600"
                            },
                            {
                                title: t('about.quality'),
                                description: t('about.qualityDescription'),
                                icon: Star,
                                color: "from-accent-500 to-accent-600"
                            },
                            {
                                title: t('about.hospitality'),
                                description: t('about.hospitalityDescription'),
                                icon: Heart,
                                color: "from-secondary-500 to-secondary-600"
                            },
                        ].map((value, index) => (
                            <motion.div
                                key={value.title}
                                initial={{ opacity: 0, y: 50, scale: 0.9 }}
                                whileInView={{ opacity: 1, y: 0, scale: 1 }}
                                transition={{ duration: 0.6, delay: index * 0.2 }}
                                viewport={{ once: true }}
                                className="group"
                            >
                                <div className="relative bg-white dark:bg-gray-800 rounded-3xl p-8 shadow-xl hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 border border-gray-100 dark:border-gray-700 overflow-hidden">
                                    {/* Background Pattern */}
                                    <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-primary-100 to-accent-100 dark:from-primary-900/20 dark:to-accent-900/20 rounded-full -translate-y-16 translate-x-16 opacity-50"></div>

                                    {/* Icon */}
                                    <div className="relative z-10 mb-8">
                                        <div className={`w-20 h-20 bg-gradient-to-br ${value.color} rounded-3xl flex items-center justify-center mx-auto group-hover:scale-110 transition-transform duration-300 shadow-lg`}>
                                            <value.icon className="w-10 h-10 text-white" />
                                            <div className="absolute inset-0 bg-white/10 rounded-3xl"></div>
                                        </div>
                                    </div>

                                    {/* Content */}
                                    <div className="relative z-10 text-center">
                                        <h3 className="text-xl font-serif font-bold mb-4 text-gray-900 dark:text-white">
                                            {value.title}
                                        </h3>
                                        <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                                            {value.description}
                                        </p>
                                    </div>

                                    {/* Hover Glow Effect */}
                                    <div className="absolute inset-0 rounded-3xl bg-gradient-to-r from-primary-500/5 to-accent-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"></div>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Enhanced Team Section */}
            <section className="relative section-padding bg-gradient-to-br from-white via-gray-50 to-white dark:from-gray-800 dark:via-gray-900 dark:to-gray-800">
                {/* Background Pattern */}
                <div className="absolute inset-0 opacity-3">
                    <div className="w-full h-full" style={{
                        backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23E44893' fill-opacity='0.1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
                    }}></div>
                </div>

                <div className="relative container mx-auto max-w-7xl container-padding">
                    <motion.div
                        initial={{ opacity: 0, y: 50 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                        viewport={{ once: true }}
                        className="text-center mb-20"
                    >
                        <div className="inline-flex items-center space-x-2 bg-secondary/10 text-secondary-600 px-4 py-2 rounded-full text-sm font-medium mb-6">
                            <Users className="w-4 h-4" />
                            <span>{t('about.meetOurTeam')}</span>
                        </div>
                        <h2 className="text-3xl md:text-4xl font-serif font-bold mb-6 leading-tight text-gray-900 dark:text-white">
                            {t('about.passionatePeople')}
                        </h2>
                        <p className="text-lg text-gray-600 dark:text-gray-300 max-w-3xl mx-auto leading-relaxed">
                            {t('about.meetTalentedIndividuals')}
                        </p>
                    </motion.div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-12">
                        {[
                            {
                                name: t('about.chefRajeshKumar'),
                                role: t('about.executiveChef'),
                                image: "/images/chef_1.jpg",
                                description: t('about.chefRajeshDescription'),
                                specialty: t('about.internationalCuisine'),
                                experience: "15+ Years"
                            },
                            {
                                name: t('about.chefPriyaSharma'),
                                role: t('about.sousChef'),
                                image: "/images/chef_2.jpg",
                                description: t('about.chefPriyaDescription'),
                                specialty: t('about.southIndianCurries'),
                                experience: "10+ Years"
                            },
                            {
                                name: t('about.amitPatel'),
                                role: t('about.restaurantManager'),
                                image: "/images/chef_3.jpg",
                                description: t('about.amitDescription'),
                                specialty: t('about.guestExperience'),
                                experience: "8+ Years"
                            },
                        ].map((member, index) => (
                            <motion.div
                                key={member.name}
                                initial={{ opacity: 0, y: 50, scale: 0.9 }}
                                whileInView={{ opacity: 1, y: 0, scale: 1 }}
                                transition={{ duration: 0.6, delay: index * 0.2 }}
                                viewport={{ once: true }}
                                className="group"
                            >
                                <div className="relative bg-white dark:bg-gray-800 rounded-3xl overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 border border-gray-100 dark:border-gray-700">
                                    {/* Image Section */}
                                    <div className="relative h-80 overflow-hidden">
                                        <img
                                            src={member.image}
                                            alt={member.name}
                                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                                        />
                                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent"></div>

                                        {/* Experience Badge */}
                                        <div className="absolute top-4 right-4 bg-white/90 dark:bg-gray-900/90 rounded-full px-3 py-1">
                                            <span className="text-xs font-semibold text-gray-900 dark:text-white">{member.experience}</span>
                                        </div>

                                        {/* Specialty Badge */}
                                        <div className="absolute bottom-4 left-4 bg-primary-600/90 text-white px-3 py-1 rounded-full">
                                            <span className="text-xs font-medium">{member.specialty}</span>
                                        </div>
                                    </div>

                                    {/* Content Section */}
                                    <div className="p-8 text-center">
                                        <h3 className="text-xl font-serif font-bold mb-2 text-gray-900 dark:text-white">
                                            {member.name}
                                        </h3>
                                        <p className="text-primary-600 font-semibold mb-4 text-base">
                                            {member.role}
                                        </p>
                                        <p className="text-gray-600 dark:text-gray-300 leading-relaxed text-sm">
                                            {member.description}
                                        </p>
                                    </div>

                                    {/* Hover Glow Effect */}
                                    <div className="absolute inset-0 rounded-3xl bg-gradient-to-r from-primary-500/5 to-secondary-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"></div>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            <Footer />
        </div>
    )
}

export default About
